import "@/App.css";
import { useEffect, useState } from "react";
// IMPORTANTE: Importamos o HashRouter aqui
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { LocationProvider } from "@/lib/location";
import { AuthModalProvider } from "@/lib/authModal";
import { AppConfigProvider } from "@/lib/appConfig";
import { NotificationsProvider } from "@/lib/notifications";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import AuthModal from "@/components/AuthModal";
import IntroductionLoader from "@/components/IntroductionLoader";
import ForcedUpdate from "@/components/ForcedUpdate";
import SmartAppBanner from "@/components/SmartAppBanner";
import api from "@/lib/api";
import { initNative, checkForcedUpdate } from "@/lib/native";
import { getGeoPosition, checkGeoPermission } from "@/lib/geolocation";
import { initSoundUnlock } from "@/lib/notify";

import Home from "@/pages/Home";
import Search from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import ListingForm from "@/pages/ListingForm";
import MyListings from "@/pages/MyListings";
import MyComplements from "@/pages/MyComplements";
import MyBookings from "@/pages/MyBookings";
import DisputeCenter from "@/pages/DisputeCenter";
import BookingSuccess from "@/pages/BookingSuccess";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Addresses from "@/pages/Addresses";
import Wallet from "@/pages/Wallet";
import Admin from "@/pages/Admin";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import Recompensas from "@/pages/Recompensas";
import PublicProfile from "@/pages/PublicProfile";
import AchievementCelebration from "@/components/AchievementCelebration";

function App() {
  const [introDone, setIntroDone] = useState(() => {
    try {
      return sessionStorage.getItem("aj_intro_done") === "1";
    } catch {
      return false;
    }
  });
  const [bootBusy, setBootBusy] = useState(!introDone);
  const [forceUpdate, setForceUpdate] = useState(null);

  useEffect(() => { initNative(); initSoundUnlock(); }, []);

  // Atualização obrigatória: no app nativo, checa a versão mínima e bloqueia se estiver desatualizado.
  useEffect(() => {
    checkForcedUpdate(async () => (await api.get("/app/version-config")).data)
      .then((info) => { if (info) setForceUpdate(info); })
      .catch(() => { /* em dúvida, não bloqueia */ });
  }, []);

  // V14.6: registra visita (anônima ou logada) p/ métricas de acesso por cidade
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        let vid = localStorage.getItem("aj_visitor_id");
        if (!vid) {
          vid = (window.crypto?.randomUUID?.() || ("v" + Date.now() + Math.random().toString(36).slice(2)));
          localStorage.setItem("aj_visitor_id", vid);
        }
        let city = null, uf = null;
        try { const loc = JSON.parse(localStorage.getItem("aj_location_v2") || "{}"); city = loc.city; uf = loc.uf; } catch { /* noop */ }
        const authenticated = !!localStorage.getItem("aj_token_v1");
        api.post("/analytics/visit", { visitor_id: vid, city, uf, authenticated }).catch(() => {});
      } catch { /* noop */ }
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  // V14.0: captura o código de indicação (?ref=CODE) e guarda p/ usar no cadastro
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      let ref = params.get("ref");
      if (!ref && window.location.hash.includes("?")) {
        ref = new URLSearchParams(window.location.hash.split("?")[1]).get("ref");
      }
      if (ref) localStorage.setItem("aj_ref_code", ref.trim().toUpperCase().slice(0, 16));
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (introDone) return;
    let cancelled = false;
    const hardCap = setTimeout(() => { if (!cancelled) setBootBusy(false); }, 2200);
    
    Promise.allSettled([
      api.get("/auth/me").catch(() => null),
      // V18.9.5 — Boot prefetch de localização: SÓ tenta se a permissão já foi
      // concedida (não dispara prompt no cold-start do app nativo).
      (async () => {
        try {
          const perm = await checkGeoPermission();
          if (perm !== "granted") return null;
          const p = await Promise.race([
            getGeoPosition({ timeout: 2000, maximumAge: 60 * 60 * 1000 }),
            new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 2000)),
          ]);
          return p;
        } catch { return null; }
      })(),
      api.get("/listings", { params: { limit: 12 } }).catch(() => null),
    ]).finally(() => { if (!cancelled) setBootBusy(false); });
    
    return () => { cancelled = true; clearTimeout(hardCap); };
  }, [introDone]);

  const finishIntro = () => {
    try { sessionStorage.setItem("aj_intro_done", "1"); } catch {}
    setIntroDone(true);
  };

  return (
    <div className="App min-h-screen bg-[#F8F9FA]">
      {forceUpdate && <ForcedUpdate info={forceUpdate} />}
      <HashRouter>
        <LocationProvider>
          <AuthProvider>
            <AppConfigProvider>
            <NotificationsProvider>
              <AuthModalProvider>
                {!introDone && (
                  <IntroductionLoader onDone={finishIntro} busy={bootBusy} />
                )}
                <SmartAppBanner />
                <Header />
                <main className="with-bottom-nav">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/buscar" element={<Search />} />
                    <Route path="/itens/:id" element={<ListingDetail />} />
                    <Route path="/novo-anuncio" element={<ListingForm />} />
                    <Route path="/editar-anuncio/:id" element={<ListingForm />} />
                    <Route path="/meus-anuncios" element={<MyListings />} />
                    <Route path="/meus-adicionais" element={<MyComplements />} />
                    <Route path="/minhas-reservas" element={<MyBookings />} />
                    <Route path="/disputas" element={<DisputeCenter />} />
                    <Route path="/disputas/:id" element={<DisputeCenter />} />
                    <Route path="/perfil" element={<Profile />} />
                    <Route path="/perfil/editar" element={<EditProfile />} />
                    <Route path="/enderecos" element={<Addresses />} />
                    <Route path="/pagamentos" element={<Navigate to="/carteira" replace />} />
                    <Route path="/carteira" element={<Wallet />} />
                    <Route path="/recompensas" element={<Recompensas />} />
                    <Route path="/u/:userId" element={<PublicProfile />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/termos" element={<Terms />} />
                    <Route path="/exclusao-de-conta" element={<DataDeletion />} />
                    <Route path="/reservas/sucesso" element={<BookingSuccess />} />
                    <Route path="/esqueci-senha" element={<ForgotPassword />} />
                    <Route path="/redefinir-senha" element={<ResetPassword />} />
                  </Routes>
                </main>
                <BottomNav />
                <AuthModal />
                <AchievementCelebration />
                <Toaster position="top-center" richColors closeButton offset={{ top: "76px" }} mobileOffset={{ top: "calc(env(safe-area-inset-top) + 72px)" }} />
              </AuthModalProvider>
            </NotificationsProvider>
            </AppConfigProvider>
          </AuthProvider>
        </LocationProvider>
      </HashRouter>
    </div>
  );
}

export default App;